import { isRouteErrorResponse, useRouteError } from 'react-router-dom'

import { ClientErrorPage, NotFoundPage } from './ErrorPage'

/**
 * The router's `errorElement`.
 *
 * It used to be `<InternalServerErrorPage />` directly, which was wrong in two
 * ways that cost real debugging time:
 *
 *  1. `errorElement` catches CLIENT-side throws — a render error, or a loader
 *     rejection. The server is usually never involved. Telling the reader
 *     "our server hit an unexpected issue" points them at the one place the
 *     fault is NOT, and it has now cost two investigations: the empty
 *     `planPreview` crash (see `workflowGraph.ts` — "every API call in those
 *     traces returned 200") and the UI-01 file-attach crash, which was chased
 *     through both backend tiers while every server log was clean.
 *
 *     So the page rendered here is `ClientErrorPage`, which carries no HTTP
 *     status and blames no one — in EVERY environment, not just dev. A
 *     customer-facing page should still be honest about WHERE the fault was;
 *     only the stack trace is dev-only.
 *
 *  2. It discarded the error. React Router hands it to us via `useRouteError()`,
 *     and we dropped it on the floor, so the only copy lived in a console the
 *     reporter had no reason to open.
 *
 * A thrown Response keeps its real status, so a loader's 404 renders as a 404
 * rather than a server fault.
 *
 * The error is always written to `console.error`. That is console VISIBILITY,
 * not telemetry: no error-tracking backend receives it, so this log lives only
 * in the affected user's browser.
 */
export function RouteErrorBoundary() {
  const error = useRouteError()

  // Written in every environment; nothing durable captures it (see above).
  console.error('[route-error]', error)

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />
  }

  const detail =
    error instanceof Error
      ? `${error.name}: ${error.message}\n\n${error.stack ?? ''}`
      : typeof error === 'string'
        ? error
        : (() => {
            try {
              return JSON.stringify(error, null, 2)
            } catch {
              return String(error)
            }
          })()

  return (
    <>
      <ClientErrorPage />
      {import.meta.env.DEV && (
        <div className="fixed inset-x-0 bottom-0 max-h-[45vh] overflow-auto border-t-4 border-red-500 bg-red-50 p-4 text-left dark:bg-red-950">
          <p className="mb-2 text-sm font-semibold text-red-700 dark:text-red-300">
            Dev only — the actual error React Router caught (this block is not in production
            builds):
          </p>
          <pre className="whitespace-pre-wrap break-words font-mono text-xs text-red-900 dark:text-red-200">
            {detail}
          </pre>
        </div>
      )}
    </>
  )
}

export default RouteErrorBoundary
